export type PromiseState<T> =
  | { type: 'resolved'; value: T }
  | { type: 'rejected'; error: unknown; value?: T }
  | { type: 'loading'; value?: T }
  | { type: 'initial'; value?: T };

// If we're too lazy to create `{get someKey() { return object["someKey"] }}`
export const trackObject = <O extends object>(obj: () => O): O => {
  return new Proxy(
    {},
    {
      get: (_obj, key: string) => {
        return obj()[key as keyof O];
      },
    },
  ) as O;
};

export const usePromise = <T>(
  trackedPromise: () => Promise<T> | null | undefined,
): PromiseState<T> => {
  const activePromise = $derived.by(trackedPromise);
  let promiseState = $state<PromiseState<T>>({ type: 'initial' });
  let latestPromiseValue = $state<T | undefined>(undefined);
  $effect(() => {
    if (!activePromise) {
      promiseState = { type: 'initial' };
      return;
    }
    let canceled = false;
    promiseState = { type: 'loading' };
    activePromise
      .then((res) => {
        if (!canceled) {
          promiseState = { type: 'resolved', value: res };
          latestPromiseValue = res;
        }
      })
      .catch((err) => {
        if (!canceled) {
          promiseState = { type: 'rejected', error: err };
        }
      });
    return () => {
      canceled = true;
    };
  });
  return trackObject(() => ({ value: latestPromiseValue, ...promiseState }));
};
